import { useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * One realtime channel per topic shared by every mounted hook. supabase-js refuses a second
 * `postgres_changes` callback on a topic that is already subscribed ("cannot add postgres_changes callbacks
 * … after subscribe()"), so two components watching the same tables (dashboard preview + request modals,
 * inbox + sidebar badge) must share the subscription: the first mount opens it, the last unmount closes it.
 */

interface SharedChannel {
  channel: RealtimeChannel;
  listeners: Set<() => void>;
}

const channels = new Map<string, SharedChannel>();

export function subscribeSharedChanges(
  topic: string,
  tables: readonly string[],
  listener: () => void,
): () => void {
  let shared = channels.get(topic);
  if (!shared) {
    const listeners = new Set<() => void>();
    let channel = supabase.channel(topic);
    for (const table of tables) {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        for (const fn of listeners) fn();
      });
    }
    channel.subscribe();
    shared = { channel, listeners };
    channels.set(topic, shared);
  }
  shared.listeners.add(listener);
  return () => {
    const current = channels.get(topic);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size === 0) {
      channels.delete(topic);
      void supabase.removeChannel(current.channel);
    }
  };
}

/** Number of open shared channels (tests). */
export function openSharedChannelCount(): number {
  return channels.size;
}

/** Subscribes for the component's lifetime; `listener` is read through a ref-like closure on every event. */
export function useSharedChanges(
  topic: string,
  tables: readonly string[],
  listener: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    return subscribeSharedChanges(topic, tables, listener);
    // tables is a module constant per call site; listener identity changes are harmless (same invalidation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, enabled]);
}
